import {
  Box,
  Button,
  Card,
  colors,
  Divider,
  Drawer,
  IconButton,
  Link,
  Popover,
  Typography,
} from "@mui/material";
import ClaimCardStyles from "./CalimCardStyles";
import {
  IClaim,
  IClaimTransaction,
  IGetClaimTransactionDocumentContentRequestParams,
} from "../../../../models/user/dashboard/Dashboard";
import { FC, useCallback, useEffect, useState } from "react";
import moment from "moment";
import {
  ALL_AGENT_ROLE_CODES,
  dateFormat,
  featuresCodes,
} from "../../../../constants/Constants";
import { Timeline, timelineOppositeContentClasses } from "@mui/lab";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import {
  GetClaimTransactionDocumentContent,
  GetClaimTransactions,
} from "../../DashboardAction";
import { RootState } from "../../../../store/store";
import Document from "../../../PolicyDetails/policyTabComponents/Documents/Document/Document";
import { IDocument } from "../../../../models/user/policyDetails/Document";
import {
  getBufferData,
  isFeatureAvailable,
  getCookie,
} from "../../../../utils/Utility";
import { setClaimTransactionDocumentContent } from "../../DashboardSlice";
import CustomTimeline from "../../../../common/CustomTimeline/CustomTimeline";
import CustomIcon from "../../../../common/CustomIcon/CustomIcon";
import { CloseOutlined } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { getFeatures } from "../../../../admin/Features/featuresActions";
import ClaimTracking from "../../../../Agents/ClaimTracking/ClaimTracking";
import CustomDrawer from "../../../../common/CustomDrawer/CustomDrawer";

interface props {
  claim: IClaim;
}

const ClaimCard: FC<props> = ({ claim }) => {
  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const {
    claimTransactions,
    claimDocuments,
    claimTransactionDocumentContent,
    carrierClaimStatus,
    initiatedDate,
    carrierPolicyNo,
    claimDescription,
    updateDate,
  } = useAppSelector((state: RootState) => state.dashboard);
  const { isSuccess, calledReducerType, isError } = useAppSelector(
    (store: RootState) => store.common
  );
  const { features } = useAppSelector((state: RootState) => state.features);

  const [currentClickFile, setCurrentClickFile] = useState<IDocument | null>(
    null
  );
  const [claimActivityDialogOpen, setClaimActivityDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const open = Boolean(anchorEl);

  const getIcon = (lobCode: string) => {
    switch (lobCode) {
      case "PA":
        return <CustomIcon iconName="Car" />;

      case "HO":
        return <CustomIcon iconName="Home" />;
    }
  };

  const handleContactLinkClick = useCallback(
    (event: any) => setAnchorEl(event.currentTarget),
    []
  );

  const handleClaimViewDetails = (
    claimId: string,
    carrier: string,
    system: string,
    lobCode: string
  ) => {
    if (claimId && carrier && system && lobCode) {
      dispatch(
        GetClaimTransactions({ claimId, carrier, system, lobCode })
      ).then(() => setClaimActivityDialogOpen(true));
    }
  };

  const handleFileClick = (type: string, documentData: IDocument) => {
    dispatch(setClaimTransactionDocumentContent(null));
    setCurrentClickFile(documentData);
    const requestParams: IGetClaimTransactionDocumentContentRequestParams = {
      claimId: claim.claimId,
      // documentId: documentData?.documentId,
      documentId: "Claims_Document",
    };
    dispatch(GetClaimTransactionDocumentContent(requestParams));
  };

  const getFeatureCodes = (featureCode: string) => {
    if (!featureCode || !claim.lobCode) {
      return;
    }
    const name: any =
      featuresCodes[featureCode as keyof typeof getFeatureCodes];
    return name[claim.lobCode];
  };

  useEffect(() => {
    if (
      isSuccess &&
      calledReducerType === "dashboard/GetClaimTransactionDocumentContent" &&
      claimTransactionDocumentContent &&
      currentClickFile
    ) {
      const bufferArray = getBufferData(claimTransactionDocumentContent);
      const url = URL.createObjectURL(
        new Blob([bufferArray], { type: "application/pdf" })
      );

      const downloadLink = document.createElement("a");
      downloadLink.download = `${currentClickFile?.name}-${currentClickFile?.addDate}-${currentClickFile?.addTime}.pdf`;
      downloadLink.href = url;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    }
  }, [
    calledReducerType,
    isSuccess,
    claimTransactionDocumentContent,
    currentClickFile,
  ]);

  useEffect(() => {
    if (
      isError &&
      calledReducerType === "dashboard/GetClaimTransactionDocumentContent"
    ) {
      alert(`Error in downloading document`);
    }
  }, [calledReducerType, isError]);

  return (
    <>
      <Card
        id="Claim-Card"
        data-test-id="test-Claim-Card"
        aria-label="Claim Card"
        sx={ClaimCardStyles.claimsCard}
      >
        {claim.risk ? (
          <Box
            id="Claimcard-Risk-Info"
            data-test-id="test-Claimcard-Risk-Info"
            aria-label="Risk Information"
            sx={ClaimCardStyles.containerOne}
          >
            <Typography
              id="Claimcard-Risk-Details"
              data-test-id="test-claimcard_risk-details"
              aria-label="Risk Details"
              sx={ClaimCardStyles.claimsCardItemHeading}
            >
              {claim.lobCode === "PA"
                ? `${claim.risk.manufactureYr}-${claim.risk.make}-${claim.risk.model}`
                : `${claim.risk.make}`}
            </Typography>
          </Box>
        ) : (
          <></>
        )}
        <Box
          id="Claimcard-Content"
          data-test-id="test-Claimcard-Content"
          aria-label="Claim Content"
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: "20px",
            width: "100%",
            "@media (max-width:600px)": { flexDirection: "column" },
          }}
        >
          {getIcon(claim.lobCode)}
          <Box
            id="Claimcard-Details"
            data-test-id="test-Claimcard-Details"
            aria-label="Claim Details"
            sx={ClaimCardStyles.containerTwo}
          >
            <Box
              id="Claimcard-Policy-Info"
              data-test-id="test-Claimcard-Policy-Info"
              aria-label="Policy Information"
              sx={ClaimCardStyles.claimPolicyName}
            >
              <Typography
                id="Claimcard-Policy-Number"
                data-test-id="test-Claimcard-Policy-Number"
                aria-label="Policy Number Value"
                sx={ClaimCardStyles.claimsCardItemHeading}
              >
                {claim.carrierPolicyNo}
              </Typography>
              <Typography
                id="Claimcard-Policy-Label"
                data-test-id="test-Claimcard-Policy-Label"
                aria-label="Policy Number Label"
                sx={ClaimCardStyles.claimsCartItemContent}
              >
                {t("Policy Number")}
              </Typography>
            </Box>
            <Box
              id="Claimcard-Claim-Number"
              data-test-id="test-Claimcard-Claim-Number"
              aria-label="Claim Number Section"
              sx={{
                "@media (max-width:1200px)": {
                  gridArea: "claimNumber",
                },
              }}
            >
              <Typography
                id="Claimcard-Claim-Number-Value"
                data-test-id="test-Claimcard-Claim-Number-Value"
                aria-label="Claim Number Value"
                sx={ClaimCardStyles.claimsCardItemHeading}
              >
                {claim.carrierClaimNo}
              </Typography>
              <Typography
                id="Claimcard-Claim-Number-Label"
                data-test-id="test-Claimcard-Claim-Number-Label"
                aria-label="Claim Number Label"
                sx={ClaimCardStyles.claimsCartItemContent}
              >
                {t("Claim Number")}
              </Typography>
            </Box>
            <Box
              id="Claimcard-Status"
              data-test-id="test-Claimcard-Status"
              aria-label="Claim Status Section"
              sx={{
                "@media (max-width:1200px)": {
                  gridArea: "verification",
                },
              }}
            >
              <Typography
                sx={{
                  ...ClaimCardStyles.claimsCardItemHeading,
                  color: ALL_AGENT_ROLE_CODES.includes(getCookie("roleCode"))
                    ? claim.carrierClaimStatus === "Open"
                      ? colors.green[500]
                      : claim.carrierClaimStatus === "Submitted"
                      ? colors.orange[500]
                      : claim.carrierClaimStatus === "Closed"
                      ? colors.green[500]
                      : "inherit"
                    : "inherit",
                  fontSize: "1.2rem",
                }}
              >
                {claim.carrierClaimStatus}
              </Typography>

              <Typography
                id="Claimcard-Status-Label"
                data-test-id="test-Claimcard-Status-Label"
                aria-label="Claim Status Label"
                sx={ClaimCardStyles.claimsCartItemContent}
              >
                {t("Claim Status")}
              </Typography>
            </Box>
            <Box
              id="Claimcard-Updated-Date"
              data-test-id="test-Claimcard-Updated-Date"
              aria-label="Updated Date Section"
              sx={{
                "@media (max-width:1200px)": {
                  gridArea: "updatedOn",
                },
              }}
            >
              <Typography
                id="Claimcard-Updated-Date-Value"
                data-test-id="test-Claimcard-Updated-Date-Value"
                aria-label="Updated Date Value"
                sx={ClaimCardStyles.claimsCardItemHeading}
              >
                {moment(claim.updateDate).format(dateFormat)}
              </Typography>
              <Typography
                id="Claimcard-Updated-Date-Label"
                data-test-id="test-Claimcard-Updated-Date-Label"
                aria-label="Updated Date Label"
                sx={ClaimCardStyles.claimsCartItemContent}
              >
                {t("Updated On")}
              </Typography>
            </Box>
            <Box
              id="Claimcard-Initiated-Date"
              data-test-id="test-Claimcard-Initiated-Date"
              aria-label="Initiated Date Section"
              sx={{
                "@media (max-width:1200px)": {
                  gridArea: "claimInitiated",
                },
              }}
            >
              <Typography
                id="Claimcard-Initiated-Date-Value"
                data-test-id="test-Claimcard-Initiated-Date-Value"
                aria-label="Initiated Date Value"
                sx={ClaimCardStyles.claimsCardItemHeading}
              >
                {moment(claim.initiatedDate).format(dateFormat)}
              </Typography>
              <Typography
                id="Claimcard-Initiated-Date-Label"
                data-test-id="test-Claimcard-Initiated-Date-Label"
                aria-label="Initiated Date Label"
                sx={ClaimCardStyles.claimsCartItemContent}
              >
                {t("Claim Initiated")}
              </Typography>
            </Box>
            <Box
              id="Claimcard-Contact"
              data-test-id="test-Claimcard-Contact"
              aria-label="Contact Section"
              sx={{
                "@media (max-width:1200px)": {
                  gridArea: "contactPerson",
                },
              }}
            >
              <Link
                id="Claimcard-Contact-Link"
                data-test-id="test-Claimcard-Contact-Link"
                aria-label="Contact Person Link"
                sx={{
                  ...ClaimCardStyles.claimsCardItemHeading,
                  cursor: "pointer",
                }}
                onClick={(event) => handleContactLinkClick(event)}
              >
                {/* {claim?.adjusterId &&
                claim.carrierClaimStatus !== "Closed" &&
                claim.adjusterDetails &&
                claim.adjusterDetails.nameInfo
                  ? claim?.adjusterDetails?.nameInfo
                  : claim.adjusterDetails && claim.adjusterDetails.nameInfo
                  ? claim.adjusterDetails.nameInfo.split(",")[1]
                  : ""} */}
                {claim &&
                claim.adjusterDetails &&
                claim.adjusterDetails.nameInfo &&
                claim.adjusterDetails.nameInfo.includes(",")
                  ? claim.adjusterDetails.nameInfo.split(",")[1]
                  : claim &&
                    claim.adjusterDetails &&
                    claim.adjusterDetails.nameInfo
                  ? claim.adjusterDetails.nameInfo
                  : ""}
              </Link>
              <Typography
                id="Claimcard-Contact-Label"
                data-test-id="test-Claimcard-Contact-Label"
                aria-label="Contact Person Label"
                sx={ClaimCardStyles.claimsCartItemContent}
              >
                {t("Contact Person")}
              </Typography>
            </Box>
            {isFeatureAvailable({
              featureCodes: getFeatureCodes("Track Your Claim"),
              features,
            }) ? (
              <Box
                id="Claimcard-Action"
                data-test-id="test-Claimcard-Action"
                aria-label="Claim Action"
                sx={ClaimCardStyles.claimsCardButtonContainer}
              >
                <Button
                  id={
                    claim.carrierClaimStatus === "Closed"
                      ? "View_Details-btn"
                      : "Track_Your_Claim-btn"
                  }
                  data-test-id={
                    claim.carrierClaimStatus === "Closed"
                      ? "test-View-Details"
                      : "test-Track-Your-Claim"
                  }
                  aria-label={
                    claim.carrierClaimStatus === "Closed"
                      ? "View Details Button"
                      : "Track Your Claim Button"
                  }
                  variant="contained"
                  onClick={() =>
                    handleClaimViewDetails(
                      claim.claimId,
                      claim.carrier,
                      claim.system,
                      claim.lobCode
                    )
                  }
                >
                  {claim.carrierClaimStatus === "Closed"
                    ? t("View Details")
                    : t("Track Your Claim")}
                </Button>
              </Box>
            ) : (
              <></>
            )}
          </Box>
        </Box>
      </Card>
      <Popover
        id="Claimcard-Contact-Popover"
        data-test-id="test-Claimcard-Contact-Popover"
        aria-label="Contact Information Popover"
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Box
          id="Claimcard-Contact-Popover-Content"
          data-test-id="test-Claimcard-Contact-Popover-Content"
          aria-label="Contact Information Content"
          sx={ClaimCardStyles.contactPopoverContent}
        >
          <Box>
            <Typography
              id="Claimcard-Contact-Name-Label"
              data-test-id="test-Claimcard-Contact-Name-Label"
              aria-label="Contact Name Label"
              sx={ClaimCardStyles.claimsCartItemContent}
            >
              {t("Name")}
            </Typography>
            <Typography
              id="Claimcard-Contact-Name-Value"
              data-test-id="test-Claimcard-Contact-Name-Value"
              aria-label="Contact Name Value"
              sx={ClaimCardStyles.claimsCardItemHeading}
            >
              {claim && claim.adjusterDetails
                ? claim.adjusterDetails.nameInfo
                : ""}
            </Typography>
          </Box>
          {claim?.adjusterDetails?.emailInfo ? (
            <Box>
              <Typography
                id="Claimcard-Contact-Email-Label"
                data-test-id="test-Claimcard-Contact-Email-Label"
                aria-label="Contact Email Label"
                sx={ClaimCardStyles.claimsCartItemContent}
              >
                {t("Email Id")}
              </Typography>
              <Typography
                id="Claimcard-Contact-Email-Value"
                data-test-id="test-Claimcard-Contact-Email-Value"
                aria-label="Contact Email Value"
                sx={ClaimCardStyles.claimsCardItemHeading}
              >
                {claim && claim.adjusterDetails
                  ? claim.adjusterDetails.emailInfo
                  : ""}
              </Typography>
            </Box>
          ) : (
            <></>
          )}
          <Box>
            <Typography
              id="Claimcard-Contact-Phone-Label"
              data-test-id="test-Claimcard-Contact-Phone-Label"
              aria-label="Contact Phone Label"
              sx={ClaimCardStyles.claimsCartItemContent}
            >
              {t("Mobile Number")}
            </Typography>
            <Typography
              id="Claimcard-Contact-Phone-Value"
              data-test-id="test-Claimcard-Contact-Phone-Value"
              aria-label="Contact Phone Value"
              sx={ClaimCardStyles.claimsCardItemHeading}
            >
              {claim?.adjusterDetails?.phoneInfo?.length > 0
                ? claim?.adjusterDetails?.phoneInfo
                : "-"}
            </Typography>
          </Box>
        </Box>
      </Popover>

      <CustomDrawer
        open={claimActivityDialogOpen}
        onClose={() => setClaimActivityDialogOpen(false)}
        title={`${claim.carrierClaimNo} - ${claim.carrierClaimStatus}`}
        content={
          <Box sx={{ display: "grid", gap: "20px" }}>
            {getCookie("roleCode") === "Producer" ? (
              <ClaimTracking
                carrierClaimStatus={carrierClaimStatus}
                initiatedDate={initiatedDate}
                carrierPolicyNo={carrierPolicyNo}
                claimDescription={claimDescription}
                updateDate={updateDate}
                claim={claim}
                claimTransactions={claimTransactions}
              />
            ) : (
              <Box sx={{ display: "grid", gap: "10px" }}>
                <Typography
                  sx={{
                    color: "#383D4E",
                    fontSize: "20px",
                    fontWeight: 400,
                    fontFamily: "Playfair Display",
                  }}
                >
                  {t("Transactions")}
                </Typography>
                {claimTransactions.length > 0 ? (
                  <Timeline
                    sx={{
                      [`& .${timelineOppositeContentClasses.root}`]: {
                        flex: 0.2,
                      },
                      zIndex: 1,
                    }}
                  >
                    {claimTransactions.map(
                      (item: IClaimTransaction, index: number) => (
                        <CustomTimeline
                          key={`claim-transaction-${index}`}
                          oppositeContent={
                            <Box
                              sx={{
                                display: "flex",
                                height: "100%",
                                alignItems: "center",
                                justifyContent: "flex-end",
                              }}
                            >
                              <Typography>
                                {moment(new Date(item.transactionDate)).format(
                                  dateFormat
                                )}
                              </Typography>
                            </Box>
                          }
                          oppositeContentStyle={{
                            color: "#1D252B",
                            fontSize: "18px",
                            fontFamily: "Noto Sans",
                            fontWeight: "300",
                            "@media(max-width:599px)": {
                              fontSize: "0.8rem",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              fontFamily: "Noto Sans",
                            },
                            "@media (min-width: 1025px) and (max-width: 1366px)":
                              {
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              height: "100%",
                              justifyContent: "center",
                            }}
                          >
                            <Typography
                              sx={{
                                color: "#1D252B",
                                fontSize: "18px",
                                fontFamily: "Noto Sans",
                                fontWeight: "300",
                                textOverflow: "ellipsis",
                                fontStyle: "normal",
                                lineHeight: "110%",
                                "@media (max-width:599px)": {
                                  fontSize: "0.8rem",
                                },
                              }}
                            >
                              {item.transactionDescription}
                            </Typography>
                            {item.adjusterDetails ? (
                              <Link
                                sx={{
                                  ...ClaimCardStyles.claimsCartItemContent,
                                  cursor: "pointer",
                                }}
                                onClick={handleContactLinkClick}
                              >
                                {t("Contact Person")}
                              </Link>
                            ) : (
                              <></>
                            )}
                          </Box>
                        </CustomTimeline>
                      )
                    )}
                  </Timeline>
                ) : (
                  <Typography
                    id="Claimcard-No-Documents"
                    data-test-id="test-Claimcard-No-Documents"
                    aria-label="No Documents Message"
                    sx={{
                      color: "#1D252B",
                      fontSize: "18px",
                      fontFamily: "Noto Sans",
                      fontWeight: "300",
                      "@media(max-width:599px)": {
                        fontSize: "14px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        fontFamily: '"Noto Sans"',
                      },
                      "@media (min-width: 1025px) and (max-width: 1366px)": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    }}
                  >
                    {t("No transactions found")}
                  </Typography>
                )}
              </Box>
            )}
            {isFeatureAvailable({
              featureCodes: getFeatureCodes("Documents"),
              features,
            }) ? (
              <Box sx={{ display: "grid", gap: "10px" }}>
                <Typography
                  sx={{
                    color: "#383D4E",
                    fontSize: "20px",
                    fontWeight: 400,
                    fontFamily: "Playfair Display",
                  }}
                >
                  {t("Documents")}
                </Typography>
                <Box>
                  {claimDocuments.length > 0 ? (
                    claimDocuments.map((document, index) => (
                      <Document
                        key={`doc-list-${index}`}
                        documentData={document}
                        handleFileClick={handleFileClick}
                        view={false}
                      />
                    ))
                  ) : (
                    <Typography
                      sx={{
                        color: "#1D252B",
                        fontSize: "18px",
                        fontFamily: "Noto Sans",
                        fontWeight: "300",
                        "@media(max-width:599px)": {
                          fontSize: "14px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          fontFamily: '"Noto Sans"',
                        },
                        "@media (min-width: 1025px) and (max-width: 1366px)": {
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        },
                      }}
                    >
                      {t("No Documents Available")}
                    </Typography>
                  )}
                </Box>
              </Box>
            ) : (
              <></>
            )}
          </Box>
        }
        drawerStyles={{
          "& .MuiDrawer-paper": {
            padding: "0 20px 20px 20px",
            width: "50%",
            "@media (max-width:768px)": {
              width: "100%",
            },
          },
        }}
      />
    </>
  );
};

export default ClaimCard;
